import entityManager, { type Entity } from '../lib/entityManager';
import { TUNE } from '../lib/config';
import {
  HexPosition,
  ActorStats,
  TakesTurns,
  IntendsToAct,
  Dead,
} from '../components';
import getLogger from '../lib/logger';
import Action from '../lib/Action';
import { ActionType } from '../lib/Action';
import TickResolutionContext from '../lib/TickResolutionContext';
import ProjectedOccupancy from '../lib/ProjectedOccupancy';

const logger = getLogger('ActionSystem');

export default class ActionSystem {
  public resolveTick(): { resolvedActions: Action[], intendingActors: Entity[], animationPlan: any[][] } {
    logger.debug("--- Action System: Tick Start ---");
    const { pendingActions, intendingActors, initialOccupancy } = this._gatherActions();

    const { resolvedActions, animationPlan } = this._resolveActionsInQueue(pendingActions, initialOccupancy);

    // After resolution, determine who dies from the outcomes and add to the animation plan.
    const newlyDead = new Set<Entity>();
    const healthTotals = new Map<number, number>(); // track health during resolution

    for (const action of resolvedActions) {
      for (const outcome of action.outcomes) {
        if (outcome.type === ActionType.ATTACK) {
          const target = outcome.target;
          if (!target.hasComponent(ActorStats) || target.hasComponent(Dead)) continue;

          const currentHealth = healthTotals.get(target.id) ?? target.getComponent(ActorStats).health;
          const newHealth = currentHealth - outcome.damage;
          healthTotals.set(target.id, newHealth);

          if (newHealth <= 0) {
            newlyDead.add(target);
          }
        }
      }
    }

    if (newlyDead.size > 0) {
      const deathAnimations: any[] = [];
      for (const deadEntity of newlyDead) {
        deathAnimations.push({ type: 'DEATH', entityId: deadEntity.id });
      }
      // Add death animations as a new, final stage of the plan.
      animationPlan.push(deathAnimations);
    }

    return { resolvedActions, intendingActors, animationPlan };
  }

  public applyOutcomes(resolvedActions: Action[], intendingActors: Entity[]): void {
    this._executeOutcomes(resolvedActions);
    this._deductEnergy(intendingActors);

    // Clean up the action components now that they have been processed.
    for (const action of resolvedActions) {
      action.entity.removeComponent(IntendsToAct);
    }

    logger.debug("--- Action System: Tick End ---");
  }

  private _gatherActions(): { pendingActions: Action[], intendingActors: Entity[], initialOccupancy: Map<string, Entity> } {
    const pendingActions: Action[] = [];
    const intendingActors: Entity[] = [];

    const actorsById = new Map();
    entityManager.query([ActorStats]).forEach((e: Entity) => actorsById.set(e.id, e));

    entityManager.query([TakesTurns, IntendsToAct]).forEach((entity: Entity) => {
      const intentComponent = entity.getComponent(IntendsToAct);
      if (!intentComponent) return;

      const { actionType, details, sourceComponentName } = intentComponent;

      const rehydratedDetails = { ...details };
      if (rehydratedDetails.targetId) {
        rehydratedDetails.target = actorsById.get(rehydratedDetails.targetId);
      }

      const action = new Action(entity, actionType, rehydratedDetails, sourceComponentName);
      action.minimumAnimationStage = 0;
      pendingActions.push(action);
      intendingActors.push(entity);
    });

    if (pendingActions.length > 0) {
      logger.debug(`Gathered ${pendingActions.length} actions:`, pendingActions.map(a => a.toString()));
    }

    const initialOccupancy = new Map<string, Entity>();
    entityManager.query([ActorStats, HexPosition]).forEach((entity: Entity) => {
      const pos = entity.getComponent(HexPosition);
      if (!pos) return;
      initialOccupancy.set(pos.coords.key, entity);
    });

    if (pendingActions.length > 0) {
      logger.debug(
        `Found ${initialOccupancy.size} initial occupants.`,
        Array.from(initialOccupancy.values()).map(v => v.toString())
      );
    }

    return { pendingActions, intendingActors, initialOccupancy };
  }

  private _resolveActionsInQueue(allActions: Action[], initialOccupancy: Map<string, Entity>): { resolvedActions: Action[], animationPlan: any[][] } {
    const projectedOccupancy = new ProjectedOccupancy();
    projectedOccupancy.setInitialOccupancy(Array.from(initialOccupancy.values()));
    const context = new TickResolutionContext(allActions, projectedOccupancy);
    return context.resolve();
  }

  private _deductEnergy(intendingActors: Entity[]): void {
    for (const entity of intendingActors) {
      if (entity.hasComponent(Dead)) continue;

      const takesTurns = entity.getComponent(TakesTurns);
      if (takesTurns) {
        takesTurns.energy -= TUNE.ACTION_COST;
      }
    }
  }

  private _executeOutcomes(actions: Action[]): void {
    if (actions.length > 0) {
      logger.debug("Executing outcomes...");
    }

    for (const action of actions) {
      const { entity, outcomes } = action;

      // An entity that's already dead (from a previous tick) or can't take turns cannot act.
      if (entity.hasComponent(Dead) || !entity.hasComponent(TakesTurns)) {
        continue;
      }

      if (outcomes.length === 0) {
        logger.debug(`[EXECUTE] ${entity.toString()} has no outcomes, idles by default.`);
        continue;
      }

      for (const outcome of outcomes) {
        switch (outcome.type) {
          case ActionType.ATTACK: {
            const target = outcome.target;
            if (target && target.hasComponent(ActorStats)) {
              logger.debug(`[EXECUTE] ${entity.toString()} attacks ${target.toString()} for ${outcome.damage} damage.`);
              const targetStats = target.getComponent(ActorStats);
              targetStats.health -= outcome.damage;
              if (targetStats.health <= 0 && !target.hasComponent(Dead)) {
                logger.debug(`[EXECUTE] DEAD! ${target.toString()} has been defeated.`);
                target.addComponent(Dead);
                target.removeComponent(TakesTurns);
              }
            }
            break;
          }
          case ActionType.MOVE: {
            logger.debug(`[EXECUTE] ${entity.toString()} moves to: ${outcome.coords.toString()}`);
            const position = entity.getComponent(HexPosition)!;
            position.coords = outcome.coords;
            break;
          }
          case ActionType.TURN: {
            logger.debug(`[EXECUTE] ${entity.toString()} turns to bearing ${outcome.bearing}.`);
            const position = entity.getComponent(HexPosition)!;
            position.bearing = outcome.bearing;
            break;
          }
          case ActionType.MISS: {
            logger.debug(`[EXECUTE] ${entity.toString()} fires a ranged attack and misses.`);
            break;
          }
          case ActionType.IDLE:
          default: {
            logger.debug(`[EXECUTE] ${entity.toString()} idles.`);
            break;
          }
        }
      }
    }
  }
}
